import { InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';

/**
 * An interface used to define the FeedstockQuantities class structure
 */
export interface FeedstockQuantitiesInterface {
  isAnimal: boolean;
  mass: UnitValue;
  period: UnitValue;
  numAnimals: UnitValue;
  manureProduction: UnitValue;
}

/**
 * A class that contains members for feedstock quantities
 */
export class FeedstockQuantities {
  isAnimal: boolean;
  mass: UnitValue;
  period: UnitValue;
  numAnimals: UnitValue;
  manureProduction: UnitValue;

  /**
   * constructs the FeedstockQuantities class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockQuantitiesInterface = {
    isAnimal: false,
    mass: new UnitValue(),
    period: new UnitValue(),
    numAnimals: new UnitValue(),
    manureProduction: new UnitValue(),
  }) {
    this.isAnimal = param.isAnimal;
    this.mass = new UnitValue(param.mass);
    this.period = new UnitValue(param.period);
    this.numAnimals = new UnitValue(param.numAnimals);
    this.manureProduction = new UnitValue(param.manureProduction);
  }

  /**
   * returns a separate instance of the provided FeedstockQuantities obj
   * @param feedstockQuantities will be returned as a separate instance
   */
  static deepCopy(feedstockQuantities: FeedstockQuantities) {
    let copy;
    if (feedstockQuantities) {
      copy = new FeedstockQuantities(feedstockQuantities);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockCharacteristics');
  }

  /**
   * returns an array of InputsSummaryItem that contains all available inputs. The inputs available depend
   * on if the isAnimal member is true or false.
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockQuantitiesPropName.isAnimal, this.isAnimal, [...path, 'isAnimal']);
    if (!this.isAnimal) {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockQuantitiesPropName.mass, this.mass, [...path, 'mass']);
    } else {
      inputs = InputSummaryUtils.performPush(inputs, FeedstockQuantitiesPropName.numAnimals, this.numAnimals, [...path, 'numAnimals']);
      inputs = InputSummaryUtils.performPush(inputs, FeedstockQuantitiesPropName.manureProduction, this.manureProduction, [...path, 'manureProduction']);
    }
    inputs = InputSummaryUtils.performPush(inputs, FeedstockQuantitiesPropName.period, this.period, [...path, 'period']);
    return inputs;
  }

  // TODO
  getMass(): UnitValue {
    if (this.isAnimal) {
      if (this.manureProduction && this.numAnimals) {
        const totalAmount = this.manureProduction.value * this.numAnimals.value;
        return new UnitValue({value: totalAmount, unit: this.manureProduction.unit});
      }
    } else {
      return UnitValue.deepCopy(this.mass);
    }
    console.error('unable to get mass from feedstock, returning null');
    return null;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockQuantities class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockQuantitiesPropName {
  isAnimal = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.IS_ANIMAL',
  mass = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.MASS',
  period = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.PERIOD',
  numAnimals = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.NUM_ANIMALS',
  manureProduction = 'INPUT_SUMMARY.FEEDSTOCK.QUANTITIES.MANURE_PRODUCTION',
  id_isAnimal = 'is material type animal',
  id_mass = 'mass',
  id_period = 'period',
  id_numAnimals = 'number of animals',
  id_manureProduction = 'waste production per animal'
}
